import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const datetimeSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'invalid datetime');
const moneySchema = z.string().regex(/^-?(0|[1-9][0-9]*)\.[0-9]{4}$/).refine((value) => value !== '-0.0000');
const currencySchema = z.string().regex(/^[A-Z]{3}$/);
const displayTextSchema = z.string().min(1).max(200);

export const eventOutSchema = z.object({
  event_id: uuidSchema,
  version: z.number().int().positive(),
  name: displayTextSchema,
  kind: z.enum(['trip', 'project', 'other']),
  start_date: dateSchema,
  end_date: dateSchema,
  location: displayTextSchema.nullable(),
  is_business: z.boolean(),
  attached_item_count: z.number().int().nonnegative(),
  totals: z.record(currencySchema, moneySchema),
  total: moneySchema.nullable(),
  currency: currencySchema.nullable(),
  totals_complete: z.boolean(),
  created_at: datetimeSchema,
  updated_at: datetimeSchema,
}).strict().superRefine((event, context) => {
  if ((event.total === null) !== (event.currency === null)) context.addIssue({ code: 'custom', path: ['total'], message: 'total and currency must be paired' });
  if (event.start_date > event.end_date) context.addIssue({ code: 'custom', path: ['start_date'], message: 'event dates are reversed' });
});
export type EventOut = z.infer<typeof eventOutSchema>;

export const eventDetailSchema = eventOutSchema.extend({
  items: z.array(z.object({
    line_item_id: uuidSchema,
    version: z.number().int().positive(),
    posted_day: dateSchema,
    amount: moneySchema,
    currency: currencySchema,
    merchant: z.string().min(1).max(160),
  }).strict()).max(200),
}).strict();
export type EventDetail = z.infer<typeof eventDetailSchema>;

export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const response = await client.get('/api/events');
      return parseEnvelope(z.array(eventOutSchema), response.data).data;
    },
    retry: false,
  });
}

export function useEvent(eventId: string | null | undefined) {
  return useQuery({
    queryKey: ['events', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const response = await client.get(`/api/events/${uuidSchema.parse(eventId)}`);
      return parseEnvelope(eventDetailSchema, response.data).data;
    },
    retry: false,
  });
}
